<template>
    <q-page class="bg-white">
        <loader :task="task" />
        <tabs
            v-if="client"
            :key="client.id"
            :client="client"
            :tab="tab"
            style="margin-top: 4.5rem"
        />
        <q-page-sticky expand position="top">
            <details-bar v-if="client" :client="client" class="bg-white q-px-lg fit shadow-1"/>
        </q-page-sticky>
    </q-page>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DetailedClient } from '@/type';
import Loader from '@/components/loading/AsyncLinearProgress.vue';
import eventBus from '@/messaging/event-bus';
import {
  PostSaved,
  PostEscalated,
  PostResolved,
  PostTrashed,
  PostRestored,
  PostMovedToTimeline,
  PostRemovedFromTimeline,
  ReportGenerated,
  ReportDeleted,
} from '@/messaging/events';
import {
  status,
  isUnreviewed,
  isOnTimeline,
  isFlagged,
} from '@/social/post/post';
import ShowPostMixin from '@/social/post/ShowPostMixin';
import { name } from '@/client/client-filter';
import DetailsBar from './DetailsBar.vue';
import repository from '../client-repository';
import Tabs from './Tabs.vue';

  interface Data {
    task: Promise<DetailedClient> | null,
    client: DetailedClient | null,
  }

export default defineComponent({
  meta() {
    return {
      // @ts-ignore
      title: this.pageTitle,
    };
  },
  components: { DetailsBar, Tabs, Loader },
  mixins: [ShowPostMixin],
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    tab: {
      type: String,
      default: 'overview',
    },
  },
  data(): Data {
    return {
      task: null,
      client: null,
    };
  },
  watch: {
    clientId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  computed: {
    pageTitle(): string {
      return (this.client) ? `${name(this.client)} Profile` : 'Client Profile';
    },
  },
  methods: {
    load() {
      this.task = repository.find(this.clientId);
      this.task.then((client: DetailedClient) => {
        this.client = client;
      });
    },
    removeMinHeight() {
      return { height: 'auto' };
    },
    onPostSaved(event: PostSaved) {
      if (this.client && this.client.id === event.post.client.id) {
        this.client.totalUnreviewed -= 1;
        this.client.totalTimeline += 1;
      }
    },
    onPostEscalated(event: PostEscalated) {
      if (this.client && this.client.id === event.post.client.id) {
        this.client.totalEscalated += 1;
        if (isUnreviewed(event.oldPost.status)) {
          this.client.totalUnreviewed -= 1;
        }
      }
    },
    onPostResolved(event: PostResolved) {
      if (!this.client || this.client.id !== event.post.client.id) return;
      this.client.totalEscalated -= 1;
    },
    onPostTrashed(event: PostTrashed) {
      const { post } = event;
      if (!this.client || this.client.id !== post.client.id) return;
      if (post.status === status.UNREVIEWED) {
        this.client.totalUnreviewed -= 1;
      }
      if (post.status === status.ESCALATED) {
        this.client.totalEscalated -= 1;
      }
      if (isOnTimeline(post)) {
        this.client.totalTimeline -= 1;
      }
      if (isFlagged(post)) {
        this.client.totalFlagged -= 1;
      }
    },
    onPostRestored(event: PostRestored) {
      const { post } = event;
      if (!this.client || this.client.id !== post.client.id) return;
      if (post.status === status.UNREVIEWED) {
        this.client.totalUnreviewed += 1;
      }
      if (post.status === status.ESCALATED) {
        this.client.totalEscalated += 1;
      }
      if (post.status === status.ESCALATED) {
        this.client.totalEscalated += 1;
      }
      if (isOnTimeline(post)) {
        this.client.totalTimeline += 1;
      }
      if (isFlagged(post)) {
        this.client.totalFlagged += 1;
      }
    },
    onPostMovedToTimeline(event: PostMovedToTimeline) {
      const { post } = event;
      if (!this.client || this.client.id !== post.client.id) return;
      this.client.totalTimeline += 1;
    },
    onPostRemovedFromTimeline(event: PostRemovedFromTimeline) {
      const { post } = event;
      if (!this.client || this.client.id !== post.client.id) return;
      this.client.totalTimeline -= 1;
    },
    onReportGenerated() {
      if (this.client) {
        this.client.totalReports += 1;
      }
    },
    onReportDeleted() {
      if (this.client) {
        this.client.totalReports -= 1;
      }
    },
  },
  created() {
    eventBus.$on(PostSaved.eventName, this.onPostSaved);
    eventBus.$on(PostEscalated.eventName, this.onPostEscalated);
    eventBus.$on(PostResolved.eventName, this.onPostResolved);
    eventBus.$on(PostTrashed.eventName, this.onPostTrashed);
    eventBus.$on(PostRestored.eventName, this.onPostRestored);
    eventBus.$on(PostMovedToTimeline.eventName, this.onPostMovedToTimeline);
    eventBus.$on(PostRemovedFromTimeline.eventName, this.onPostRemovedFromTimeline);
    eventBus.$on(ReportGenerated.eventName, this.onReportGenerated);
    eventBus.$on(ReportDeleted.eventName, this.onReportDeleted);
  },
  beforeUnmount(): void {
    eventBus.$off(PostSaved.eventName, this.onPostSaved);
    eventBus.$off(PostEscalated.eventName, this.onPostEscalated);
    eventBus.$off(PostResolved.eventName, this.onPostResolved);
    eventBus.$off(PostTrashed.eventName, this.onPostTrashed);
    eventBus.$off(PostRestored.eventName, this.onPostRestored);
    eventBus.$off(PostMovedToTimeline.eventName, this.onPostMovedToTimeline);
    eventBus.$off(PostRemovedFromTimeline.eventName, this.onPostRemovedFromTimeline);
    eventBus.$off(ReportGenerated.eventName, this.onReportGenerated);
    eventBus.$off(ReportDeleted.eventName, this.onReportDeleted);
  },
});
</script>
